import { useEffect, useMemo, useRef, useState } from 'react';
import { RangeKeyDict } from 'react-date-range';
import {
  MdCheck,
  MdKeyboardArrowDown,
  MdOutlineFilterAlt,
  MdOutlineSettingsBackupRestore,
} from 'react-icons/md';
import { useLocation, useSearchParams } from 'react-router-dom';

import {
  DASHBOARD_SUMMARY_DAYS_DIFFERENCE,
  FontSize,
  FontWeight,
  LocalStorageKeys,
  STATUSES,
  TEST_IDS,
  deliveryModes,
  getDefaultDashboardFilter,
  getDefaultDashboardSearch,
  routes,
  salesModes,
  shipmentsStatuses,
} from 'commons';
import { Button, Checkbox, DateRangePicker, Select, Text } from 'components';
import { endOfDay, getTime, startOfDay, toDate } from 'date-fns';
import { useFormik } from 'formik';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from 'helpers';
import { formatTimestamp, getFinalDate, getInitialDate } from 'helpers/date';
import { useAuth } from 'hooks';
import {
  DashboardFilterForm,
  FormOption,
  HomeFilterParam,
  QueryParam,
} from 'types';
import { DashboardFilterStore } from 'zustand-store';

import * as S from '../styles';

export function DashboardMoreFilter() {
  const { selectedStore } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    dashboardFilter,
    filteredBrand,
    filteredStore,
    setDashboardFilter,
    setDashboardSearch,
    reset,
  } = DashboardFilterStore.useStore();
  const [isOpen, setIsOpen] = useState(false);
  const mainRef = useRef<HTMLDivElement>(null);

  function clearUrlSearchParamFilter() {
    searchParams.delete(QueryParam.filter);
    setSearchParams(searchParams);
    removeLocalStorageItem(LocalStorageKeys.hadUrlDashboardFilterApplied);
  }

  const {
    values,
    handleChange,
    handleSubmit,
    submitForm,
    resetForm,
    handleReset,
    setFieldValue,
    setValues,
  } = useFormik<DashboardFilterForm>({
    initialValues: dashboardFilter,

    onSubmit: values => {
      clearUrlSearchParamFilter();

      setIsOpen(false);

      const currValues = { ...values };
      const currStatusesToString = String(currValues.statuses);
      const currDeliveryModesToString = String(currValues.deliveryModes);
      currValues.statuses = currStatusesToString
        .split(',')
        .filter(deliveryMode => deliveryMode !== '');
      currValues.deliveryModes = currDeliveryModesToString
        .split(',')
        .filter(deliveryMode => deliveryMode !== '');

      setDashboardFilter(currValues);
    },

    onReset: (_, { setValues }) => {
      clearUrlSearchParamFilter();

      const defaultFilter = getDefaultDashboardFilter();
      const defaultDashboardSearch = getDefaultDashboardSearch();

      setDashboardFilter(defaultFilter);
      setDashboardSearch(defaultDashboardSearch);

      setTimeout(() => {
        setValues(defaultFilter);
      }, 100);

      setIsOpen(false);
    },
  });

  function changeDateRange(dateRange: RangeKeyDict) {
    const { startDate, endDate } = dateRange.range1;

    if (!startDate || !endDate) return;

    const start = startOfDay(startDate);
    const end = endOfDay(endDate);

    setFieldValue('startDate', getTime(start));
    setFieldValue('endDate', getTime(end));
  }

  useEffect(() => {
    searchParams.delete(QueryParam.filter);
    setSearchParams(searchParams);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStore]);

  useEffect(() => {
    function filterByUrl() {
      const { pathname, search } = location;

      const params = new URLSearchParams(search);
      const filter = params.get(QueryParam.filter);

      if (filter) {
        reset({ store: filteredStore, brand: filteredBrand });
      }

      const startDate = getInitialDate({
        timeDifference: DASHBOARD_SUMMARY_DAYS_DIFFERENCE,
      });
      const endDate = getFinalDate();

      const defaultFilter = getDefaultDashboardFilter();

      switch (filter) {
        case HomeFilterParam.servicePending:
          setLocalStorageItem(
            LocalStorageKeys.hadUrlDashboardFilterApplied,
            true
          );
          const { AVAILABLE_FOR_STORE, RESERVED_BY_STORE } = STATUSES;
          const servicePendingFilter: DashboardFilterForm = {
            ...defaultFilter,
            startDate,
            endDate,
            statuses: [AVAILABLE_FOR_STORE.value, RESERVED_BY_STORE.value],
          };

          setValues(servicePendingFilter);
          setDashboardFilter(servicePendingFilter);
          break;

        case HomeFilterParam.waitingForCustomer:
          setLocalStorageItem(
            LocalStorageKeys.hadUrlDashboardFilterApplied,
            true
          );
          const { READY_FOR_CUSTOMER_PICKUP_IN_STORE } = STATUSES;

          const waitingForCustomerFilter: DashboardFilterForm = {
            ...defaultFilter,
            startDate,
            endDate,
            statuses: [READY_FOR_CUSTOMER_PICKUP_IN_STORE.value],
          };

          setValues(waitingForCustomerFilter);
          setDashboardFilter(waitingForCustomerFilter);
          break;

        case HomeFilterParam.deliveryFail:
          setLocalStorageItem(
            LocalStorageKeys.hadUrlDashboardFilterApplied,
            true
          );
          const { CLARIFY_DELIVERY_FAIL, COLLECT_FAIL } = STATUSES;

          const deliveryFailFilter: DashboardFilterForm = {
            ...defaultFilter,
            startDate,
            endDate,
            statuses: [CLARIFY_DELIVERY_FAIL.value, COLLECT_FAIL.value],
          };

          setValues(deliveryFailFilter);
          setDashboardFilter(deliveryFailFilter);
          break;

        case HomeFilterParam.pickupByDriver:
          setLocalStorageItem(
            LocalStorageKeys.hadUrlDashboardFilterApplied,
            true
          );
          const { PICKUP_BY_DRIVER } = STATUSES;

          const pickupByDriverFilter: DashboardFilterForm = {
            ...defaultFilter,
            startDate,
            endDate,
            statuses: [PICKUP_BY_DRIVER.value],
          };

          setValues(pickupByDriverFilter);
          setDashboardFilter(pickupByDriverFilter);
          break;

        default:
          if (
            getLocalStorageItem(
              LocalStorageKeys.hadUrlDashboardFilterApplied
            ) &&
            routes.dashboard.path === pathname
          ) {
            clearUrlSearchParamFilter();
            setValues(defaultFilter);
            setDashboardFilter(defaultFilter);
          }
          break;
      }
    }

    filterByUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    if (values.filterDate !== dashboardFilter.filterDate) {
      setValues(dashboardFilter);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardFilter.filterDate]);

  const { day: startDateDay, month: startDateMonth } = formatTimestamp(
    values.startDate
  );
  const { day: endDateDay, month: endDateMonth } = formatTimestamp(
    values.endDate
  );

  const formattedStartDate = `${startDateDay}/${startDateMonth}`;
  const formattedEndDate = `${endDateDay}/${endDateMonth}`;

  const {
    statuses,
    salesModes: selectedSalesModes,
    deliveryModes: selectedDeliveryModes,
  } = dashboardFilter;

  const renderOptions = salesModes.map(({ label, value }) => {
    return (
      <S.Label key={value}>
        <Checkbox
          name="salesModes"
          id="salesModes"
          variant="primary"
          value={value}
          checked={values.salesModes.includes(value)}
          onChange={handleChange}
        />
        <Text>{label}</Text>
      </S.Label>
    );
  });

  const selectedStatusesOptions: FormOption[] = useMemo(() => {
    let currentStatus: FormOption;

    return shipmentsStatuses
      .map(({ label, value }) => {
        const splitValues = value.split(',');
        const splitStatuses = String(values.statuses).split(',');
        const populator = (validator: boolean) =>
          validator ? { label, value } : { label: '', value: '' };

        if (splitValues.length > 1) {
          currentStatus = splitValues.map(splitValue =>
            populator(splitStatuses.includes(splitValue))
          )[0];
        } else {
          currentStatus = populator(splitStatuses.includes(value));
        }

        return currentStatus;
      })
      .filter(status => status.value !== '');
  }, [values.statuses]);

  const selectedDeliveryModesOptions = useMemo(
    () =>
      deliveryModes.filter(({ value }) =>
        String(values.deliveryModes).split(',').includes(value)
      ),
    [values.deliveryModes]
  );

  function renderNumberOfSelectedStatuses(statuses: string[]) {
    const includesDelivered = (status: string) =>
      [
        STATUSES.DELIVERED.value,
        STATUSES.DELIVERED_BY_DRIVER.value,
        STATUSES.MANUALLY_DELIVERED.value,
      ].includes(status);

    let filteredStatus = statuses.filter(
      status => !includesDelivered(status)
    ).length;

    const containsOtherStatuses = statuses.some(status =>
      includesDelivered(status)
    );

    if (containsOtherStatuses) {
      filteredStatus++;
    }

    return (
      <Text size={FontSize.font12}>
        Status: <strong>{filteredStatus}</strong>
      </Text>
    );
  }

  return (
    <S.Main data-testid={TEST_IDS.CONSIGNMENT_FILTER} ref={mainRef}>
      <S.DropdownButton onClick={() => setIsOpen(!isOpen)} $isOpen={isOpen}>
        <div className="wrapper">
          <MdOutlineFilterAlt size={18} />
          <Text size={FontSize.font12} weight={FontWeight.bold}>
            {formattedStartDate} a {formattedEndDate}
          </Text>
          {Boolean(selectedSalesModes.length) && (
            <Text size={FontSize.font12}>
              Modalid: <strong>{selectedSalesModes.length}</strong>
            </Text>
          )}
          {Boolean(statuses.length) && renderNumberOfSelectedStatuses(statuses)}
          {Boolean(selectedDeliveryModes.length) && (
            <Text size={FontSize.font12}>
              Transp: <strong>{selectedDeliveryModes.length}</strong>
            </Text>
          )}
        </div>
        <S.ArrowWrapper $isOpen={isOpen}>
          <MdKeyboardArrowDown size={24} />
        </S.ArrowWrapper>
      </S.DropdownButton>

      {isOpen && (
        <>
          <S.Overlay onClick={() => setIsOpen(false)}></S.Overlay>
          <S.Form
            data-testid={TEST_IDS.CONSIGNMENT_FILTER_FORM}
            onSubmit={handleSubmit}
            onReset={handleReset}
            $parentPosition={mainRef.current?.getBoundingClientRect().top}
          >
            <S.DatePickerWrapper>
              <DateRangePicker
                onChangeRange={changeDateRange}
                onSubmit={submitForm}
                onReset={resetForm}
                values={{
                  startDate: toDate(values.startDate),
                  endDate: toDate(values.endDate),
                }}
              />
            </S.DatePickerWrapper>

            <S.FormSection>
              <S.SalesMode>
                <Text size={FontSize.font12}>Modalidade</Text>
                <S.SalesModeCheckboxWrapper>
                  {renderOptions}
                </S.SalesModeCheckboxWrapper>
              </S.SalesMode>

              <S.SelectWrapper>
                <Text size={FontSize.font12}>Status</Text>
                <Select.Wrapper
                  key="statuses"
                  name="statuses"
                  onChange={handleChange}
                  options={shipmentsStatuses}
                  initialTagged={selectedStatusesOptions}
                  multiple
                  searchable
                >
                  <Select.Input placeholder="Selecionar" />

                  <Select.Tags maxTags={1} />

                  <Select.StatusArrow />

                  <Select.ClearButton />
                </Select.Wrapper>
              </S.SelectWrapper>

              <S.SelectWrapper>
                <Text size={FontSize.font12}>Transportadora</Text>
                <Select.Wrapper
                  key="deliveryModes"
                  name="deliveryModes"
                  onChange={handleChange}
                  options={deliveryModes}
                  initialTagged={selectedDeliveryModesOptions}
                  multiple
                  searchable
                >
                  <Select.Input placeholder="Selecionar" />

                  <Select.Tags maxTags={1} />

                  <Select.StatusArrow />

                  <Select.ClearButton />
                </Select.Wrapper>
              </S.SelectWrapper>
            </S.FormSection>

            <S.ButtonsWrapper>
              <Button variant="link" type="reset">
                <MdOutlineSettingsBackupRestore size={20} />
                Retornar ao Padrão
              </Button>
              <Button type="submit">
                <MdCheck size={20} />
                Aplicar
              </Button>
            </S.ButtonsWrapper>
          </S.Form>
        </>
      )}
    </S.Main>
  );
}
